import numeral from 'numeral'
import { jsPDF } from 'jspdf'
import global from '@/utils/helpers/global'
import html2canvas from 'html2canvas'

const isProduction = process.env.NODE_ENV === 'production'

let STRUK_LOGO_PATH = ''
if (isProduction) {
  STRUK_LOGO_PATH = '/assets/img/ic_launcher-web-round.png'
} else {
  STRUK_LOGO_PATH = '/assets/img/ic_launcher-web-round.png'
}


const getPublicBaseUrl = () => {
  const origin = typeof window !== 'undefined' ? window.location.origin : ''
  const publicUrl = process.env.PUBLIC_URL || ''
  return `${origin}${publicUrl}`.replace(/\/$/, '')
}

const toAbsoluteUrl = (path) => {
  const base = getPublicBaseUrl()
  if (!base) return path
  if (!path) return base
  return `${base}${path.startsWith('/') ? '' : '/'}${path}`
}

const safeHtml = (value) =>
  String(value ?? '').replace(/[&<>"']/g, (char) => {
    switch (char) {
      case '&':
        return '&amp;'
      case '<':
        return '&lt;'
      case '>':
        return '&gt;'
      case '"':
        return '&quot;'
      case "'":
        return '&#039;'
      default:
        return char
    }
  })

const formatRupiah = (value) => `Rp ${numeral(value || 0).format('0,0')}`
const formatRupiahCompact = (value) => `Rp${numeral(value || 0).format('0,0')}`
const formatNumber = (value) => numeral(parseFloat(value || 0)).format('0,0')

const formatTanggalPembayaran = ({ item, fallbackDate = new Date() }) => {
  const raw = item?.tanggal_pembayaran
  const d = raw ? new Date(raw) : fallbackDate
  if (!(d instanceof Date) || Number.isNaN(d.getTime())) return '-'
  return d.toLocaleDateString('id-ID', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

export const buildStrukTagihanHtml = ({
  pelangganKode,
  pelangganNama,
  item,
  printedAt = new Date(),
}) => {
  const logoUrl = toAbsoluteUrl(STRUK_LOGO_PATH)
  const baseHref = `${toAbsoluteUrl('/')}/`
  const periodeBulan = global.convert_month(item?.periode_bulan)
  const periodeTahun = item?.periode_tahun || ''
  const periode = `${periodeBulan} ${periodeTahun}`.trim() || '-'

  const tagihanAir = parseFloat(item?.tagihan_air || 0)
  const biayaAdmin = parseFloat(item?.biaya_admin || 0)
  const danaMeter = parseFloat(item?.dana_meter || 0)
  const denda = parseFloat(item?.denda || 0)
  const total = tagihanAir + biayaAdmin + danaMeter + denda

  const pembayaranDateText = formatTanggalPembayaran({ item, fallbackDate: printedAt })
  const alamat = item?.alamat_pelanggan || item?.alamat_lkp || '-'
  const golongan = item?.nama_golongan || item?.golongan || item?.gol_trf || '-'
  const noPembayaran = item?.no_pembayaran || item?.id || '-'
  const kasir = item?.nama_user || item?.kasir || '-'

  return `<!doctype html>
<html lang="id" style="line-height: 1.5;">
  <head>
    <meta charset="utf-8" />
    <meta http-equiv="X-UA-Compatible" content="IE=edge" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <base href="${safeHtml(baseHref)}" />
    <title>Struk Tagihan</title>
    <style>
      @media print {
        @page { size: 70mm 297mm; margin: 5px; }
        body, .page {
          margin: 0;
          box-shadow: 0;
          font-family: Helvetica, 'sans-serif', 'monospace' !important;
          font-size: 9pt;
          font-weight: lighter;
        }
        hr {
          border: none;
          border-top: 1px dotted black;
          color: #fff;
          background-color: #fff;
          height: 1px;
          width: 100%;
        }
      }

      hr {
        border: none;
        border-top: 1px dotted black;
        color: #fff;
        background-color: #fff;
        height: 1px;
        width: 100%;
      }

      body {
        background: rgb(204, 204, 204);
        font-family: Helvetica, 'sans-serif', 'monospace' !important;
        font-size: 9pt;
        font-weight: lighter;
        margin: 0;
        padding: 0;
      }

      .page {
        background: white;
        display: block;
        margin: 0 auto;
        width: 70mm;
        padding: 0;
      }

      table { border-collapse: collapse; width: 100%; }
      td, th { padding: 0 !important; vertical-align: top; }
    </style>
  </head>
  <body>
    <div class="page">
      <table style="width: 100%;">
        <tr>
          <td style="width: 20%; vertical-align: middle; text-align: center;">
            <img
              src="${safeHtml(logoUrl)}"
              alt="logo"
              style="width: 70px; height: 70px; object-fit: contain;"
            />
          </td>
          <td style="width: 80%; vertical-align: middle;">
            <div style="font-weight: bold; font-size: 13pt; line-height: 1.1;">PERUMDA AIR MINUM</div>
            <div style="font-weight: bold; font-size: 13pt; line-height: 1.1;">CILEGON MANDIRI</div>
          </td>
        </tr>
      </table>
      <div style="text-align: center; margin-top: 2px;">
        Jl. Nuri No. 1 Perumnas Cibeber Kota Cilegon
        <br />
        (0254) 378112 - www.pdamcilegon.co.id
        <br />
        NPWP : 02.249.245.8-417.000
      </div>

      <hr />

      <div style="text-align: center;">
        <div style="font-size: 11pt;">BUKTI PEMBAYARAN TAGIHAN AIR</div>
      </div>

      <div style="width: 80%; margin: 0 auto;">
        <table>
          <tbody>
            <tr>
              <td>No Pembayaran</td>
              <td>&nbsp;:&nbsp;</td>
              <td>${safeHtml(noPembayaran)}</td>
            </tr>
            <tr>
              <td>Pemakaian</td>
              <td>&nbsp;:&nbsp;</td>
              <td>${safeHtml(periode)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <hr />

      <table style="margin-left: 10px; width: 95%;">
        <tbody>
          <tr>
            <td style="width: 30%;">Nama</td>
            <td>&nbsp;:&nbsp;</td>
            <td>${safeHtml(pelangganNama || '-')}</td>
          </tr>
          <tr>
            <td style="text-align: left; vertical-align: top;">Alamat</td>
            <td style="vertical-align: top;">&nbsp;:&nbsp;</td>
            <td>${safeHtml(alamat)}</td>
          </tr>
          <tr>
            <td>No Sambung</td>
            <td>&nbsp;:&nbsp;</td>
            <td>${safeHtml(pelangganKode || '-')}</td>
          </tr>
          <tr>
            <td>Golongan</td>
            <td>&nbsp;:&nbsp;</td>
            <td>${safeHtml(golongan)}</td>
          </tr>
          <tr>
            <td>Waktu</td>
            <td>&nbsp;:&nbsp;</td>
            <td>${safeHtml(pembayaranDateText)}</td>
          </tr>
          <tr>
            <td>Kasir</td>
            <td>&nbsp;:&nbsp;</td>
            <td>${safeHtml(kasir)} / DUPLIKAT</td>
          </tr>
        </tbody>
      </table>

      <hr />

      <table style="margin-left: 10px; width: 95%;">
        <tbody>
          <tr>
            <td>
              Std Mtr (${safeHtml(formatNumber(item?.m3_akhir))} - ${safeHtml(formatNumber(item?.m3_awal))} = ${safeHtml(formatNumber(item?.m3_pemakaian))}M3)
            </td>
            <td style="width: 33%; text-align: right;">${safeHtml(formatRupiahCompact(tagihanAir))}</td>
          </tr>
          <tr>
            <td>Denda</td>
            <td style="width: 33%; text-align: right;">${safeHtml(formatRupiahCompact(denda))}</td>
          </tr>
          <tr>
            <td>Dana Meter</td>
            <td style="width: 33%; text-align: right;">${safeHtml(formatRupiahCompact(danaMeter))}</td>
          </tr>
          <tr>
            <td>Admin</td>
            <td style="width: 33%; text-align: right;">${safeHtml(formatRupiahCompact(biayaAdmin))}</td>
          </tr>
        </tbody>
      </table>

      <hr />

      <table style="margin-left: 10px; width: 95%;">
        <tr style="font-size: 12pt;">
          <td>Total Tagihan</td>
          <td style="width: 40%; text-align: right;">${safeHtml(formatRupiahCompact(total))}</td>
        </tr>
      </table>

      <hr />

      <div style="margin-left: 10px; width: 95%;">
        <table>
          <tbody>
            <tr>
              <td style="vertical-align: top;">#&nbsp;</td>
              <td>Rekening tertunggak lebih dari 2 bulan, akan ditutup tanpa pemberitahuan</td>
            </tr>
            <tr>
              <td style="vertical-align: top;">#&nbsp;</td>
              <td>http://www.pdamcilegon.co.id/cektagihan</td>
            </tr>
            <tr>
              <td style="vertical-align: top;">#&nbsp;</td>
              <td>Transaksi pembayaran tagihan ini dibebaskan PPN berdasarkan PP no. 28 Tahun 2021.</td>
            </tr>
            <tr>
              <td style="vertical-align: top;">#&nbsp;</td>
              <td>Download Sekarang Aplikasi PDAM Cilegon Mandiri di Playstore dan Appstore</td>
            </tr>
          </tbody>
        </table>
      </div>

      <hr />

      <div style="text-align: center;">- TERIMA KASIH ATAS PEMBAYARAN ANDA -</div>

      <hr />
    </div>

    <script>
      window.onload = function () {
        try { window.focus(); } catch (e) {}
        setTimeout(function () {
          try { window.print(); } catch (e) {}
        }, 200);
      };
    </script>
  </body>
</html>`
}

export const buildStrukTagihanHtmlForPdf = ({
  pelangganKode,
  pelangganNama,
  item,
  printedAt = new Date(),
}) => {
  const html = buildStrukTagihanHtml({ pelangganKode, pelangganNama, item, printedAt })
  return html.replace(/<script[\s\S]*?<\/script>/gi, '')
}

export const buildStrukTagihanPdfViaHtml = async ({
  pelangganKode,
  pelangganNama,
  item,
  printedAt = new Date(),
}) => {
  const html = buildStrukTagihanHtmlForPdf({ pelangganKode, pelangganNama, item, printedAt })
  const iframe = document.createElement('iframe')
  iframe.style.position = 'fixed'
  iframe.style.left = '-10000px'
  iframe.style.top = '0'
  iframe.style.width = '0'
  iframe.style.height = '0'
  document.body.appendChild(iframe)
  const doc = iframe.contentDocument
  doc.open()
  doc.write(html)
  doc.close()
  await new Promise((resolve) => {
    const fn = () => resolve()
    if (iframe.contentWindow.document.readyState === 'complete') resolve()
    else iframe.onload = fn
  })
  const page = iframe.contentDocument.querySelector('.page')
  const canvas = await html2canvas(page, { scale: 2, useCORS: true })
  const img = canvas.toDataURL('image/png')
  const pxPerMm = 96 / 25.4
  const pageWidthMm = 70
  const pageHeightMm = canvas.height / pxPerMm
  const pdf = new jsPDF({ unit: 'mm', format: [pageWidthMm, pageHeightMm] })
  pdf.addImage(img, 'PNG', 0, 0, pageWidthMm, pageHeightMm)
  document.body.removeChild(iframe)
  return pdf
}

export const buildStrukTagihanPdf = async ({
  pelangganKode,
  pelangganNama,
  item,
  printedAt = new Date(),
}) => {
  const doc = new jsPDF({ unit: 'mm', format: [75, 297] })

  const pageWidth = doc.internal.pageSize.getWidth()
  const left = 5
  const right = pageWidth - 5
  const contentWidth = right - left

  const loadDataUrl = async (url) => {
    const res = await fetch(url)
    if (!res.ok) throw new Error('Logo not found')
    const blob = await res.blob()
    const dataUrl = await new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(reader.result)
      reader.onerror = reject
      reader.readAsDataURL(blob)
    })
    return dataUrl
  }

  const addDivider = (y) => {
    doc.setDrawColor(0, 0, 0)
    doc.setLineWidth(0.2)
    doc.setLineDashPattern([0.8, 1.2], 0)
    doc.line(left, y, right, y)
    doc.setLineDashPattern([], 0)
  }

  const addWrappedText = ({ text, x, y, maxWidth, lineHeight = 3.6 }) => {
    const wrapped = doc.splitTextToSize(String(text || ''), maxWidth)
    doc.text(wrapped, x, y)
    return y + wrapped.length * lineHeight
  }

  const addCenteredWrappedText = ({ text, y, maxWidth, lineHeight = 3.6 }) => {
    const wrapped = doc.splitTextToSize(String(text || ''), maxWidth)
    for (const line of wrapped) {
      doc.text(String(line), pageWidth / 2, y, { align: 'center' })
      y += lineHeight
    }
    return y
  }

  const addLabelColonValue = ({ label, value, y, labelWidth = 22, gap = 2 }) => {
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(9)
    doc.setTextColor(0, 0, 0)
    doc.text(String(label || ''), left, y)
    doc.text(':', left + labelWidth, y)

    const valueX = left + labelWidth + gap
    const valueMax = right - valueX
    doc.setFont('helvetica', 'normal')
    return addWrappedText({ text: value || '-', x: valueX, y, maxWidth: valueMax, lineHeight: 3.6 })
  }

  const addCenteredKeyValue = ({ key, value, y, lineHeight = 3.8 }) => {
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(9)
    doc.setTextColor(0, 0, 0)
    const text = `${String(key || '').trim()} : ${String(value || '-').trim()}`
    const wrapped = doc.splitTextToSize(text, contentWidth)
    for (const line of wrapped) {
      doc.text(String(line), pageWidth / 2, y, { align: 'center' })
      y += lineHeight
    }
    return y
  }

  const periodeBulan = global.convert_month(item?.periode_bulan)
  const periodeTahun = item?.periode_tahun || ''
  const periode = `${periodeBulan} ${periodeTahun}`.trim() || '-'

  const tagihanAir = parseFloat(item?.tagihan_air || 0)
  const biayaAdmin = parseFloat(item?.biaya_admin || 0)
  const danaMeter = parseFloat(item?.dana_meter || 0)
  const denda = parseFloat(item?.denda || 0)
  const total = tagihanAir + biayaAdmin + danaMeter + denda

  const pembayaranDateText = formatTanggalPembayaran({ item, fallbackDate: printedAt })
  const alamat = item?.alamat_pelanggan || item?.alamat_lkp || '-'
  const golongan = item?.nama_golongan || item?.golongan || item?.gol_trf || '-'
  const noPembayaran = item?.no_pembayaran || item?.id || '-'
  const kasir = item?.nama_user || item?.kasir || '-'

  const logoAreaWidth = contentWidth * 0.2
  const textAreaX = left + logoAreaWidth
  const textAreaWidth = right - textAreaX

  const headerTop = 10
  const logoSize = Math.min(12, logoAreaWidth)
  const titleY1 = headerTop + 5.5
  const titleY2 = titleY1 + 5

  try {
    const logoDataUrl = await loadDataUrl(toAbsoluteUrl(STRUK_LOGO_PATH))
    const logoX = left + Math.max(0, (logoAreaWidth - logoSize) / 2)
    const logoY = headerTop + Math.max(0, ((titleY2 - headerTop) - logoSize) / 2)
    doc.addImage(logoDataUrl, 'PNG', logoX, logoY, logoSize, logoSize)
  } catch (e) {}

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(13)
  doc.setTextColor(0, 0, 0)
  doc.text('PERUMDA AIR MINUM', textAreaX, titleY1, { maxWidth: textAreaWidth })
  doc.text('CILEGON MANDIRI', textAreaX, titleY2, { maxWidth: textAreaWidth })

  let y = Math.max(headerTop + logoSize, titleY2 + 2) + 3
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(9)
  y = addCenteredWrappedText({
    text: 'Jl. Nuri No. 1 Perumnas Cibeber Kota Cilegon',
    y,
    maxWidth: contentWidth,
    lineHeight: 3.6,
  })
  doc.text('(0254) 378112 - www.pdamcilegon.co.id', pageWidth / 2, y, { align: 'center' })
  y += 4
  doc.text('NPWP : 02.249.245.8-417.000', pageWidth / 2, y, { align: 'center' })

  y += 4
  addDivider(y)

  y += 5
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(10)
  doc.text('BUKTI PEMBAYARAN TAGIHAN AIR', pageWidth / 2, y, { align: 'center' })

  y += 5
  doc.setFontSize(9)
  y = addCenteredKeyValue({ key: 'No Pembayaran', value: String(noPembayaran), y })
  y += 0.5
  y = addCenteredKeyValue({ key: 'Pemakaian', value: String(periode), y })

  y += 2
  addDivider(y)

  y += 5
  y = addLabelColonValue({ label: 'Nama', value: pelangganNama || '-', y })
  y += 1
  y = addLabelColonValue({ label: 'Alamat', value: alamat, y })
  y += 1
  y = addLabelColonValue({ label: 'No Sambung', value: pelangganKode || '-', y })
  y += 1
  y = addLabelColonValue({ label: 'Golongan', value: golongan, y })
  y += 1
  y = addLabelColonValue({ label: 'Waktu', value: pembayaranDateText, y })
  y += 1
  y = addLabelColonValue({ label: 'Kasir', value: `${kasir} / DUPLIKAT`, y })

  y += 2
  addDivider(y)

  y += 5
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(9)
  const stdMtrText = `Std Mtr (${formatNumber(item?.m3_akhir)} - ${formatNumber(item?.m3_awal)} = ${formatNumber(item?.m3_pemakaian)}M3)`
  const amountWidth = 18
  const textMax = contentWidth - amountWidth - 2
  const afterStd = addWrappedText({ text: stdMtrText, x: left, y, maxWidth: textMax, lineHeight: 3.6 })
  doc.text(formatRupiahCompact(tagihanAir), right, y, { align: 'right' })
  y = Math.max(afterStd, y + 4)

  y += 2
  doc.text('Denda', left, y)
  doc.text(formatRupiahCompact(denda), right, y, { align: 'right' })
  y += 4
  doc.text('Dana Meter', left, y)
  doc.text(formatRupiahCompact(danaMeter), right, y, { align: 'right' })
  y += 4
  doc.text('Admin', left, y)
  doc.text(formatRupiahCompact(biayaAdmin), right, y, { align: 'right' })
  y += 4
  doc.text('BP Tahap ke -', left, y)
  doc.text('Rp0', right, y, { align: 'right' })

  y += 3
  addDivider(y)

  y += 6
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(11)
  doc.text('Total Tagihan', left, y)
  doc.text(formatRupiahCompact(total), right, y, { align: 'right' })

  y += 4
  addDivider(y)

  y += 5
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(9)
  const notes = [
    'Rekening tertunggak lebih dari 2 bulan, akan ditutup tanpa pemberitahuan',
    'http://www.pdamcilegon.co.id/cektagihan',
    'Transaksi pembayaran tagihan ini dibebaskan PPN berdasarkan PP no. 28 Tahun 2021.',
    'Download Sekarang Aplikasi PDAM Cilegon Mandiri di Playstore dan Appstore',
  ]

  for (const note of notes) {
    doc.text('#', left, y)
    y = addWrappedText({ text: note, x: left + 4, y, maxWidth: contentWidth - 4, lineHeight: 3.6 })
    y += 1
  }

  y += 1
  addDivider(y)

  y += 6
  doc.setFontSize(9)
  doc.text('- TERIMA KASIH ATAS PEMBAYARAN ANDA -', pageWidth / 2, y, { align: 'center' })

  return doc
}
